import { useState, useEffect, useCallback } from "react";
import { isNumber } from "lodash";
import { Segment, Menu, Header, Grid, Divider, Icon, Loader } from "semantic-ui-react";
import PermitAttachmentBar from "./PermitAttachmentBar";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { FormError } from "../models/FormError";
import RequestPermitForm, { FormData } from "./RequestPermitForm";
import { trancateTime, toastError, toastSuccess, isAfter } from "../utils";
import { useHistory, useParams } from "react-router-dom";
import { Permit } from "../models/Permit";
import { PermitDataInput } from "../models/PermitDataInput";
import { AffectedServicesOption } from "../models/AffectedServicesOption";
import { Document_Data } from "../models/Document_Data";
import PermitTeamForm, { FormData as PermitTeamFormData } from "./PermitTeamForm";
import DocumentView from "./DocumentView";
import moment from "moment";
import PermitMap from "./PermitMap";
import MapWithBoundaries from "./MapWithBoundaries";
import { MapItem } from "../utils/arcgisUtils";
import { Project } from "../models/Project";
import PermitDeclarationsModal from "./PermitDeclarationsModal";
import { PermitAssignmentInput } from "../models/PermitAssignmentInput";
import { RegisterAssignment } from "../models/RegisterAssignment";
import { PermitAssignment } from "../models/PermitAssignment";
import { CalibrationCertificate } from "../models/CalibrationCertificate";
import PermitCalibrationCertificate from "./PermitCalibrationCertificate";
import { SitePhotoInput, PermitFieldValueInput, StateKey } from "../models/globalTypes";
import { SystemDocumentType } from "../models/SystemDocumentType";
import { Boundary } from "../models/Boundary";
import { SitePhoto } from "../models/SitePhoto";
import PermitSitePhotos from "./PermitSitePhotos";
import PhotoViewer from "./common/PhotoViewer";
import { UserFile } from "../models/UserFile";
import { OrgUser } from "../models/OrgUser";
import { getEffectiveSettings } from "../models/PermitTypeDocumentType";
import EditBoundaryTypeOrCommentModal from "./EditBoundaryTypeOrCommentModal";
import { createProjectMapItem } from "../utils/boundaryUtils";
import { validateCustomFields } from "./PermitFieldValuesForm";
import { TemplateField } from "../models/TemplateField";
import { TemplateFieldFields } from "../operations/fragments/__generated__/TemplateFieldFields";
import { DocumentRequirementKeyList } from "../models/DocumentRequirementKey";
import { mapPermitFieldValues } from "../models/Permit";
import { StateKeyList } from "../models/StateKey";
import { PermitType } from "../models/PermitType";

interface Props {
  organizationHasEsriIntegration?: boolean;
  requestButtonVisible?: boolean;
  saveButtonVisible?: boolean;
  requestRejectedPermitButtonsVisible?: boolean;
  permit?: Permit;
  sourcePermit?: Permit;
  currentProject?: Project;
  affectedServicesOptions?: AffectedServicesOption[];
  registerAssignments?: RegisterAssignment[];
  nextPermitNumber?: number;
  onAddPermitRequest: (
    permitDataInput: PermitDataInput,
    number: number,
    documentIds: string[],
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignments: PermitAssignmentInput[],
    calibrationCertificate: CalibrationCertificate,
    newSitePhotos: SitePhotoInput[],
    fieldValues?: PermitFieldValueInput[],
  ) => void;
  addPermitRequestStatus: AsyncActionStatus;
  onEditPermit: (
    id: string,
    permitDataInput: PermitDataInput,
    documentIds: string[],
    calibrationCertificate: CalibrationCertificate,
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignments: PermitAssignmentInput[],
    newSitePhotos: SitePhotoInput[],
    sitePhotoIds: string[],
    fieldValues?: PermitFieldValueInput[],
  ) => void;
  editPermitStatus: AsyncActionStatus;
  onRequestPermit: (
    id: string,
    permitDataInput: PermitDataInput,
    number: number,
    documentIds: string[],
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignments: PermitAssignmentInput[],
    calibrationCertificate: CalibrationCertificate,
    newSitePhotos: SitePhotoInput[],
    sitePhotoIds: string[],
    fieldValues?: PermitFieldValueInput[],
  ) => void;
  requestPermitStatus: AsyncActionStatus;
  onRequestRejectedPermit: (
    id: string,
    permitDataInput: PermitDataInput,
    documentIds: string[],
    asBuiltRequired: boolean,
    boundaries: Boundary[],
    permitAssignments: PermitAssignmentInput[],
    calibrationCertificate: CalibrationCertificate,
    newSitePhotos: SitePhotoInput[],
    sitePhotoIds: string[],
    fieldValues?: PermitFieldValueInput[],
  ) => void;
  requestRejectedPermitStatus: AsyncActionStatus;
  addNewDocumentButtonVisible?: boolean;
  permitType?: PermitType | null;
}

const RequestPermit = ({
  organizationHasEsriIntegration,
  saveButtonVisible,
  requestButtonVisible,
  requestRejectedPermitButtonsVisible,
  permit,
  sourcePermit,
  currentProject,
  affectedServicesOptions,
  registerAssignments,
  nextPermitNumber,
  onAddPermitRequest,
  addPermitRequestStatus,
  onEditPermit,
  editPermitStatus,
  onRequestPermit,
  requestPermitStatus,
  onRequestRejectedPermit,
  requestRejectedPermitStatus,
  addNewDocumentButtonVisible,
  permitType,
}: Props) => {
  const history = useHistory();

  const { orgName } = useParams<{ orgName: string }>();
  const { projectName } = useParams<{ projectName: string }>();
  const { registerTitle } = useParams<{ registerTitle: string }>();

  const permitTemplate = permit?.template ?? permitType?.template;
  const isCatAndGennyEnabled = permitTemplate?.features?.catAndGenny?.enabled ?? false;
  const isAffectedServicesEnabled = permitTemplate?.features?.affectedServices?.enabled ?? false;
  const isAsBuiltEnabled = permitTemplate?.features?.asBuilt?.enabled ?? false;
  const permitTypeRoles = permitType?.roles ?? undefined;
  const requestDeclaration = permitTemplate?.configuration?.preRequestDeclarations;
  const maxPermitDurationDays = permitTemplate?.configuration?.maxPermitDurationDays;
  const maxPermitValidInAdvanceDays = permitTemplate?.configuration?.maxPermitValidInAdvanceDays;
  const optionToSelectThePermitTeam = permitTemplate?.configuration?.optionToSelectThePermitTeamInThePermitRequest;
  const requireSitePhoto = permitTemplate?.features?.sitePhoto?.requireSitePhotoForSubmission;
  const isSitePhotoEnabled = permitTemplate?.features?.sitePhoto?.enabled ?? false;
  const mandatoryDocTypes = permitType?.documentTypes?.filter(
    (ptdt) =>
      !ptdt.documentType.isArchived &&
      getEffectiveSettings(ptdt).requirement.key === DocumentRequirementKeyList.REQUEST_AND_SUBMISSION.key,
  );
  const showPermitBoundariesOnMap = permitTemplate?.features?.gisBoundary?.enabled && organizationHasEsriIntegration;

  const [formData, setFormData] = useState<FormData>({} as FormData);
  const [permitTeamFormData, setPermitTeamFormData] = useState<PermitTeamFormData>({
    permitAssignments: [],
  } as PermitTeamFormData);
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [boundaryErrors, setBoundaryErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<Document_Data | undefined>(undefined);
  const [documents, setDocuments] = useState<Document_Data[]>([]);
  const [documentErrors, setDocumentErrors] = useState<FormError>({});
  const [calibrationCertificateErrors, setcalibrationCertificateErrors] = useState<FormError>({});
  const [previewBoundaries, setPreviewBoundaries] = useState(true);
  const [boundaries, setBoundaries] = useState<Boundary[]>([]);
  const [projectBoundaries, setProjectBoundaries] = useState<MapItem[]>([]);
  const [openSubmitModal, setOpenSubmitModal] = useState(false);
  const [declarationInitialized, setDeclarationInitialized] = useState(false);
  const [requestDeclarations, setRequestDeclarations] = useState<
    { index: number; text: string; value: boolean; readonly: boolean }[]
  >([]);
  const [calibrationCertificate, setCalibrationCertificate] = useState<CalibrationCertificate>(
    {} as CalibrationCertificate,
  );
  const [editBoundariesInProgress, setEditBoundariesInProgress] = useState(false);
  const [sitePhotos, setSitePhotos] = useState<SitePhoto[]>([]);
  const [newSitePhotos, setNewSitePhotos] = useState<SitePhotoInput[]>([]);
  const [sitePhotoErrors, setSitePhotoErrors] = useState<FormError>({});
  const [previewSitePhoto, setPreviewSitePhoto] = useState<SitePhoto | undefined>(undefined);
  const [previewNewSitePhoto, setPreviewNewSitePhoto] = useState<UserFile | undefined>(undefined);
  const [boundaryIndexToEditTypeOrComment, setBoundaryIndexToEditTypeOrComment] = useState<number | undefined>(
    undefined,
  );
  const [boundaryToEditTypeOrComment, setBoundaryToEditTypeOrComment] = useState<Boundary | undefined>(undefined);
  const [openEditBoundaryTypeOrCommentModal, setOpenEditBoundaryTypeOrCommentModal] = useState(false);
  // Resolve template: use permit's own template when editing, otherwise use the permit type template
  const resolvedTemplateFields: TemplateField[] = (
    (permit?.template?.fields ?? permitType?.template?.fields ?? []) as (TemplateFieldFields | null)[]
  )?.filter((f): f is TemplateFieldFields => f !== null);

  useEffect(() => {
    if (declarationInitialized) {
      return;
    }

    var allRequestDeclarations = new Array();

    if (mandatoryDocTypes && requestDeclaration) {
      allRequestDeclarations = mandatoryDocTypes
        .filter((t) => getEffectiveSettings(t).declaration)
        .map((t, i) => {
          return { index: i, text: getEffectiveSettings(t).declaration, value: true, readonly: true };
        });

      const declarationLength = allRequestDeclarations.length;
      allRequestDeclarations.push(
        ...requestDeclaration.map((d, i) => {
          return { index: i + declarationLength, text: d, value: false, readonly: false };
        }),
      );

      setDeclarationInitialized(true);
    }

    setRequestDeclarations(allRequestDeclarations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestDeclaration, mandatoryDocTypes]);

  useEffect(() => {
    if (!(permitType && permitTemplate)) {
      return;
    }

    if (permit) {
      initializeFromPermit(permit);
    } else if (nextPermitNumber) {
      if (sourcePermit) {
        initializeFromSourcePermit(sourcePermit);
      } else {
        initializeNewPermit();
      }
    }
    setFormErrors({} as FormError);
    setBoundaryErrors({});
    setHasSubmitted(false);
    requestPermitStatus.reset();
    requestRejectedPermitStatus.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permit, sourcePermit, nextPermitNumber, permitType, permitTemplate]);

  useEffect(() => {
    if (boundaryIndexToEditTypeOrComment !== undefined) {
      const boundary = boundaries ? boundaries[boundaryIndexToEditTypeOrComment] : undefined;
      setBoundaryToEditTypeOrComment(boundary);
      setOpenEditBoundaryTypeOrCommentModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryIndexToEditTypeOrComment]);

  function initializeFromPermit(permit: Permit) {
    setFormData({
      id: permit.id,
      number: permit.number,
      comments: permit.comments,
      contractor: permit.contractor,
      subContractor: permit.subContractor,
      locationOfWork: permit.locationOfWork,
      worksDescription: permit.worksDescription,
      from: new Date(permit.validityDuration.from),
      to: new Date(permit.validityDuration.to),
      hoursFrom: permit.validityDuration.hoursFrom,
      hoursTo: permit.validityDuration.hoursTo,
      endsNextDay: permit.validityDuration.endsNextDay ?? false,
      noAffectedServices: permit.noAffectedServices,
      affectedServices: permit.affectedServices,
      asBuiltRequired: permit.asBuiltRequired,
      fieldValues: permit.fieldValues ? mapPermitFieldValues(permit.fieldValues) : undefined,
    } as FormData);
    if (optionToSelectThePermitTeam) {
      setPermitTeamFormData({
        permitAssignments: permit.permitAssignments,
      } as PermitTeamFormData);
    } else {
      initializePermitAssignments();
    }
    setDocuments(permit.documents);
    setBoundaries(permit.areas);
    setCalibrationCertificate(permit.calibrationCertificate || {});
    setSitePhotos(permit.sitePhotos || []);
  }

  function initializeFromSourcePermit(sourcePermit: Permit) {
    setFormData({
      number: nextPermitNumber,
      comments: sourcePermit.comments,
      contractor: sourcePermit.contractor,
      subContractor: sourcePermit.subContractor,
      locationOfWork: sourcePermit.locationOfWork,
      worksDescription: sourcePermit.worksDescription,
      hoursFrom: sourcePermit.validityDuration?.hoursFrom,
      hoursTo: sourcePermit.validityDuration?.hoursTo,
      endsNextDay: sourcePermit.validityDuration?.endsNextDay ?? false,
      noAffectedServices: isAffectedServicesEnabled ? sourcePermit.noAffectedServices : true,
      affectedServices: isAffectedServicesEnabled ? sourcePermit.affectedServices : undefined,
      asBuiltRequired: isAsBuiltEnabled ? sourcePermit.asBuiltRequired : false,
      fieldValues: sourcePermit.fieldValues ? mapPermitFieldValues(sourcePermit.fieldValues) : undefined,
    } as FormData);
    if (optionToSelectThePermitTeam) {
      setPermitTeamFormData({
        permitAssignments: createPermitAssignments(sourcePermit.permitAssignments),
      } as PermitTeamFormData);
    } else {
      initializePermitAssignments();
    }
    setDocuments(sourcePermit.documents.filter((d) => d.type.key !== SystemDocumentType.ARC_GIS_DRAWING.key));
    setBoundaries(sourcePermit.areas);
    setCalibrationCertificate(sourcePermit.calibrationCertificate || {});
  }

  function initializeNewPermit() {
    setFormData({
      number: nextPermitNumber,
      hoursFrom: moment.duration("07:00").toISOString(),
      hoursTo: moment.duration("17:00").toISOString(),
      endsNextDay: false,
      noAffectedServices: !isAffectedServicesEnabled,
      asBuiltRequired: !isAsBuiltEnabled ? false : undefined,
    } as FormData);
    initializePermitAssignments();
  }

  function createPermitAssignments(sourceAssignments?: PermitAssignment[]): PermitAssignment[] {
    return (
      permitTypeRoles
        ?.filter((r) => (r.settings ?? r.orgRole.permitRoleSettings).isPermitAssignee)
        .map((r) => {
          const sourceAssignment = sourceAssignments?.find((a) => a.orgRole.id === r.orgRole.id);
          return { orgRole: r.orgRole, orgUser: sourceAssignment?.orgUser ?? null } as PermitAssignment;
        }) ?? []
    );
  }

  function initializePermitAssignments() {
    setPermitTeamFormData({
      permitAssignments: createPermitAssignments(),
    } as PermitTeamFormData);
  }

  useEffect(() => {
    if (currentProject && currentProject.boundaries) {
      setProjectBoundaries([createProjectMapItem(currentProject)]);
    }
  }, [currentProject]);

  const validateDocuments = (docs: Document_Data[]) => {
    const docError: FormError = {};

    mandatoryDocTypes?.forEach((docType) => {
      if (!docs.find((d) => d.type.key === docType.documentType.key)) {
        docError[docType.documentType.key] =
          `Please attach the required '${docType.documentType.name}' within the required documents section.`;
      }
    });

    return docError;
  };

  const validateCalibrationCertificate = (certificate: CalibrationCertificate) => {
    const calError: FormError = {};

    if (
      !certificate.catSerialNumber ||
      !certificate.catCalibrationDueDate ||
      !certificate.gennySerialNumber ||
      !certificate.gennyCalibrationDueDate
    ) {
      calError.calibrationCertificate = "Please fill in all required fields in the calibration certificate section.";
    }

    if (certificate.catCalibrationDueDate && isAfter(new Date(), certificate.catCalibrationDueDate)) {
      calError.calibrationCertificate = "The CAT calibration due date must be in the future.";
    }

    if (certificate.gennyCalibrationDueDate && isAfter(new Date(), certificate.gennyCalibrationDueDate)) {
      calError.calibrationCertificate = "The Genny calibration due date must be in the future.";
    }

    return calError;
  };

  const validateSitePhotos = (photos: SitePhoto[], newPhotos: SitePhotoInput[]) => {
    const sitePhotoError: FormError = {};

    if (isSitePhotoEnabled && requireSitePhoto && !(photos.length > 0 || newPhotos.length > 0)) {
      sitePhotoError.any = "Please add site photos before submitting your request";
    }

    return sitePhotoError;
  };

  const validateBoundaries = (currentBoundaries: Boundary[]) => {
    const boundaryError: FormError = {};

    if ((!currentBoundaries || currentBoundaries.length === 0) && showPermitBoundariesOnMap) {
      boundaryError.boundaries = "Please draw permit boundaries on map";
    }
    if (editBoundariesInProgress) {
      boundaryError.boundaries =
        "Please complete the current boundary modification by clicking outside its area on the map before saving or submitting.";
    }

    return boundaryError;
  };

  const validateFormData = (formData: FormData, targetState: StateKey) => {
    const formError: FormError = {};

    if (formData.number === undefined) {
      formError.number = "Please enter permit number";
    }

    if (!formData.worksDescription) {
      formError.worksDescription = "Please enter work description";
    }

    if (!formData.locationOfWork) {
      formError.locationOfWork = "Please enter location of work";
    }

    if (!formData.from) {
      formError.from = "Please enter date";
    }

    if (!formData.to) {
      formError.to = "Please enter date";
    }

    if (formData.from && formData.to && isAfter(formData.from, formData.to)) {
      formError.to = "To date cannot be before from date";
    }

    if (formData.from && isNumber(maxPermitValidInAdvanceDays)) {
      const maxAvailableFrom = moment().startOf("day").add(maxPermitValidInAdvanceDays, "days").toDate();
      if (isAfter(formData.from, maxAvailableFrom)) {
        formError.from = `Permit cannot be raised more than ${maxPermitValidInAdvanceDays} days in advance`;
      }
    }

    if (formData.from && formData.to && isNumber(maxPermitDurationDays)) {
      const durationDays = moment(formData.to).startOf("day").diff(moment(formData.from).startOf("day"), "days");
      if (durationDays >= maxPermitDurationDays) {
        formError.to = `Permit duration cannot exceed ${maxPermitDurationDays} days`;
      }
    }

    if (formData.hoursFrom === undefined) {
      formError.hoursFrom = "Please enter hours";
    }

    if (formData.hoursTo === undefined) {
      formError.hoursTo = "Please enter hours";
    }

    if (formData.hoursFrom !== undefined && formData.hoursTo !== undefined) {
      const hoursFromMinutes = moment.duration(formData.hoursFrom).asMinutes();
      const hoursToMinutes = moment.duration(formData.hoursTo).asMinutes();

      if (!formData.endsNextDay && hoursFromMinutes > hoursToMinutes) {
        formError.hoursFrom = "The From time cannot be after the To time";
      }

      if (formData.endsNextDay && hoursFromMinutes < hoursToMinutes) {
        formError.hoursFrom = "For overnight work, the From time must be after the To time";
      }
    }

    if (targetState === StateKeyList.REQUESTED.key) {
      if (
        !(formData.affectedServices && formData.affectedServices.length > 0) &&
        !formData.noAffectedServices &&
        isAffectedServicesEnabled
      ) {
        formError.affectedServices = "Please select affected services or confirm that there are no affected services";
      }

      if (formData.asBuiltRequired === undefined && isAsBuiltEnabled) {
        formError.asBuiltRequired = "Please specify if an as-built is required";
      }
    }

    return formError;
  };

  const validateAndSetFormErrors = (data: FormData, targetState: StateKey) => {
    let validationErrors = validateFormData(data, targetState);

    if (targetState === StateKeyList.REQUESTED.key) {
      Object.assign(
        validationErrors,
        validateCustomFields(resolvedTemplateFields, data.fieldValues ?? [], targetState),
      );
    }

    const boundaryValidationErrors = validateBoundaries(boundaries);

    setFormErrors({ ...validationErrors });
    setBoundaryErrors(boundaryValidationErrors);
    return { validationErrors: { ...validationErrors, ...boundaryValidationErrors } };
  };

  const handleSave = () => {
    setHasSaved(true);
    setHasSubmitted(false);
    setDocumentErrors({});
    setcalibrationCertificateErrors({});
    setSitePhotoErrors({});

    const { validationErrors } = validateAndSetFormErrors(formData, StateKeyList.REQUEST_IN_PROGRESS.key);

    if (Object.keys(validationErrors).length === 0) {
      const {
        number,
        comments,
        contractor,
        subContractor,
        locationOfWork,
        worksDescription,
        from,
        to,
        hoursFrom,
        hoursTo,
        endsNextDay,
        noAffectedServices,
        affectedServices,
        asBuiltRequired,
      } = formData;

      const { permitAssignments } = permitTeamFormData;
      const permitAssignmentInputs = permitAssignments.map((a) => {
        return { orgRoleId: a.orgRole.id, orgUserId: a.orgUser?.id } as PermitAssignmentInput;
      });

      const dFrom = trancateTime(from!);
      const dTo = trancateTime(to!);

      const calCertificate = {
        catSerialNumber: calibrationCertificate.catSerialNumber,
        catCalibrationDate: calibrationCertificate.catCalibrationDate,
        catCalibrationDueDate: calibrationCertificate.catCalibrationDueDate,
        gennySerialNumber: calibrationCertificate.gennySerialNumber,
        gennyCalibrationDate: calibrationCertificate.gennyCalibrationDate,
        gennyCalibrationDueDate: calibrationCertificate.gennyCalibrationDueDate,
      } as CalibrationCertificate;

      if (permit) {
        onEditPermit(
          permit.id,
          {
            comments,
            contractor,
            subContractor,
            locationOfWork,
            worksDescription,
            from: dFrom,
            to: dTo,
            hoursFrom,
            hoursTo,
            endsNextDay,
            noAffectedServices,
            affectedServices,
          },
          documents?.map((d) => d.id),
          calCertificate,
          asBuiltRequired || false,
          boundaries,
          permitAssignmentInputs,
          isSitePhotoEnabled ? newSitePhotos : [],
          isSitePhotoEnabled ? sitePhotos.map((s) => s.id) : [],
          formData.fieldValues,
        );
      } else {
        onAddPermitRequest(
          {
            comments,
            contractor,
            subContractor,
            locationOfWork,
            worksDescription,
            from: dFrom,
            to: dTo,
            hoursFrom,
            hoursTo,
            endsNextDay,
            noAffectedServices,
            affectedServices,
          },
          parseInt(number.toString()),
          documents?.map((d) => d.id),
          asBuiltRequired || false,
          boundaries,
          permitAssignmentInputs,
          calCertificate,
          isSitePhotoEnabled ? newSitePhotos : [],
          formData.fieldValues,
        );
      }
    }
  };

  const handleSubmitClick = () => {
    setHasSubmitted(true);
    setHasSaved(false);

    const { validationErrors } = validateAndSetFormErrors(formData, StateKeyList.REQUESTED.key);
    const documentValidationErrors = validateDocuments(documents);
    const calibrationCertificateValidationErrors = isCatAndGennyEnabled
      ? validateCalibrationCertificate(calibrationCertificate)
      : {};
    const sitePhotoValidationErrors = validateSitePhotos(sitePhotos, newSitePhotos);

    setDocumentErrors(documentValidationErrors);
    setcalibrationCertificateErrors(calibrationCertificateValidationErrors);
    setSitePhotoErrors(sitePhotoValidationErrors);

    if (
      Object.keys(validationErrors).length === 0 &&
      Object.keys(documentValidationErrors).length === 0 &&
      Object.keys(calibrationCertificateValidationErrors).length === 0 &&
      Object.keys(sitePhotoValidationErrors).length === 0
    ) {
      setOpenSubmitModal(true);
    }
  };

  const handleRequest = () => {
    const allDeclarationsChecked = requestDeclarations.every((item) => item.value === true);
    if (!allDeclarationsChecked) {
      toastError("All declarations must be approved, if not the permit cannot be requested");
      return;
    }

    if (allDeclarationsChecked) {
      const {
        number,
        comments,
        contractor,
        subContractor,
        locationOfWork,
        worksDescription,
        from,
        to,
        hoursFrom,
        hoursTo,
        endsNextDay,
        noAffectedServices,
        affectedServices,
        asBuiltRequired,
      } = formData;

      const { permitAssignments } = permitTeamFormData;
      const permitAssignmentInputs = permitAssignments.map((a) => {
        return { orgRoleId: a.orgRole.id, orgUserId: a.orgUser?.id } as PermitAssignmentInput;
      });

      const dFrom = trancateTime(from!);
      const dTo = trancateTime(to!);

      const calCertificate = {
        catSerialNumber: calibrationCertificate.catSerialNumber,
        catCalibrationDate: calibrationCertificate.catCalibrationDate,
        catCalibrationDueDate: calibrationCertificate.catCalibrationDueDate,
        gennySerialNumber: calibrationCertificate.gennySerialNumber,
        gennyCalibrationDate: calibrationCertificate.gennyCalibrationDate,
        gennyCalibrationDueDate: calibrationCertificate.gennyCalibrationDueDate,
      } as CalibrationCertificate;

      if (permit && permit.state.key === StateKeyList.REJECTED.key) {
        onRequestRejectedPermit(
          permit.id,
          {
            comments,
            contractor,
            subContractor,
            locationOfWork,
            worksDescription,
            from: dFrom,
            to: dTo,
            hoursFrom,
            hoursTo,
            endsNextDay,
            noAffectedServices,
            affectedServices,
          },
          documents?.map((d) => d.id),
          asBuiltRequired || false,
          boundaries,
          permitAssignmentInputs,
          calCertificate,
          isSitePhotoEnabled ? newSitePhotos : [],
          isSitePhotoEnabled ? sitePhotos.map((s) => s.id) : [],
          formData.fieldValues,
        );
      } else {
        onRequestPermit(
          permit?.id || "",
          {
            comments,
            contractor,
            subContractor,
            locationOfWork,
            worksDescription,
            from: dFrom,
            to: dTo,
            hoursFrom,
            hoursTo,
            endsNextDay,
            noAffectedServices,
            affectedServices,
          },
          parseInt(number.toString()),
          documents?.map((d) => d.id),
          asBuiltRequired || false,
          boundaries,
          permitAssignmentInputs,
          calCertificate,
          isSitePhotoEnabled ? newSitePhotos : [],
          isSitePhotoEnabled ? sitePhotos.map((s) => s.id) : [],
          formData.fieldValues,
        );
      }
    }
  };

  useEffect(() => {
    if (addPermitRequestStatus.successful) {
      toastSuccess("Permit added successfully!");
      addPermitRequestStatus.reset();
      history.push(`/${orgName}/projects/${projectName}/registers/${registerTitle}/permits`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addPermitRequestStatus.successful]);

  useEffect(() => {
    if (editPermitStatus.successful) {
      toastSuccess("Permit edited successfully!");
      editPermitStatus.reset();
      history.push(`/${orgName}/projects/${projectName}/registers/${registerTitle}/permits`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPermitStatus.successful]);

  useEffect(() => {
    if (requestPermitStatus.successful) {
      toastSuccess("Permit requested successfully!");
      requestPermitStatus.reset();
      history.push(`/${orgName}/projects/${projectName}/registers/${registerTitle}/permits`);
    } else if (requestPermitStatus.errors && requestPermitStatus.errors.length > 0) {
      toastError(requestPermitStatus.errors[0]);
      // handleSubmitModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestPermitStatus]);

  useEffect(() => {
    if (requestRejectedPermitStatus.successful) {
      toastSuccess("Permit requested successfully!");
      requestRejectedPermitStatus.reset();
      history.push(`/${orgName}/projects/${projectName}/registers/${registerTitle}/permits`);
    } else if (requestRejectedPermitStatus.errors && requestRejectedPermitStatus.errors.length > 0) {
      handleSubmitModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestRejectedPermitStatus]);

  const handleDocClicked = (doc: Document_Data) => {
    setPreviewDoc(doc);
    setPreviewBoundaries(false);
    setPreviewSitePhoto(undefined);
    setPreviewNewSitePhoto(undefined);
  };

  const handleSitePhotoClicked = (photo: SitePhoto) => {
    setPreviewSitePhoto(photo);
    setPreviewDoc(undefined);
    setPreviewBoundaries(false);
    setPreviewNewSitePhoto(undefined);
  };

  const handleNewSitePhotoClicked = (photo: UserFile) => {
    setPreviewNewSitePhoto(photo);
    setPreviewDoc(undefined);
    setPreviewBoundaries(false);
    setPreviewSitePhoto(undefined);
  };

  const handleRemoveNewSitePhoto = (id: string) => {
    const updatedNewSitePhotos = newSitePhotos.filter((p) => p.fileMetaData?.id !== id);
    setNewSitePhotos(updatedNewSitePhotos);
    if (previewNewSitePhoto && previewNewSitePhoto.id === id) {
      setPreviewNewSitePhoto(undefined);
    }

    if (hasSubmitted) {
      setSitePhotoErrors(validateSitePhotos(sitePhotos, updatedNewSitePhotos));
    }
  };

  const handleRemoveSitePhoto = (id: string) => {
    const updatedSitePhotos = sitePhotos.filter((p) => p.id !== id);
    setSitePhotos(updatedSitePhotos);
    if (previewSitePhoto && previewSitePhoto.id === id) {
      setPreviewSitePhoto(undefined);
    }

    if (hasSubmitted) {
      setSitePhotoErrors(validateSitePhotos(updatedSitePhotos, newSitePhotos));
    }
  };

  const handleAddSitePhoto = (photo: SitePhotoInput) => {
    const updatedNewSitePhotos = [...newSitePhotos, photo];
    setNewSitePhotos(updatedNewSitePhotos);

    if (hasSubmitted) {
      setSitePhotoErrors(validateSitePhotos(sitePhotos, updatedNewSitePhotos));
    }
  };

  const handleCalibrationCertificateChange = (updatedCertificate: CalibrationCertificate) => {
    setCalibrationCertificate(updatedCertificate);

    if (hasSubmitted && isCatAndGennyEnabled) {
      setcalibrationCertificateErrors(validateCalibrationCertificate(updatedCertificate));
    }
  };

  const handleFormChange = (formData: FormData) => {
    if (hasSubmitted || hasSaved) {
      validateAndSetFormErrors(
        formData,
        hasSubmitted ? StateKeyList.REQUESTED.key : StateKeyList.REQUEST_IN_PROGRESS.key,
      );
    }

    setFormData({ ...formData });
  };

  const handlePermitTeamFormChange = (formData: PermitTeamFormData) => {
    setPermitTeamFormData({ ...formData });
  };

  const handleAddDoc = (doc: Document_Data) => {
    if (documents.find((d) => d.id === doc.id)) {
      toastError("Selected document already exists");
      return;
    }

    const updatedDocuments = [...documents, doc];
    setDocuments(updatedDocuments);

    if (hasSubmitted) {
      setDocumentErrors(validateDocuments(updatedDocuments));
    }
  };

  const handleRemoveDoc = (doc: Document_Data) => {
    const updatedDocuments = documents.filter((d) => d.id !== doc.id);
    setDocuments(updatedDocuments);

    if (hasSubmitted) {
      setDocumentErrors(validateDocuments(updatedDocuments));
    }
  };

  const handleBackToListClick = () => {
    history.push(`/${orgName}/projects/${projectName}/registers/${registerTitle}/permits`);
  };

  const affectedSerOptions = () => {
    if (affectedServicesOptions) {
      if (formData.affectedServices) {
        return [...affectedServicesOptions.map((a) => a.name), ...formData.affectedServices];
      } else {
        return [...affectedServicesOptions.map((a) => a.name)];
      }
    }
  };

  const handleSubmitModalClose = () => {
    setOpenSubmitModal(false);
    setRequestDeclarations(requestDeclarations.map((item) => ({ ...item, value: item.readonly ? item.value : false })));
  };

  const handleSaveBoundaries = (newBoundaries: Boundary[]) => {
    setEditBoundariesInProgress(false);
    setBoundaries(newBoundaries);

    if (hasSubmitted || hasSaved) {
      setBoundaryErrors(validateBoundaries(newBoundaries));
    }
  };

  const handleBoundariesEditStarted = useCallback(() => {
    setEditBoundariesInProgress(true);
  }, []);

  const handleEditBoundaryTypeOrComment = (i: string) => {
    setBoundaryIndexToEditTypeOrComment(parseInt(i));
  };

  const handleEditBoundaryTypeOrCommentModalSave = (updatedBoundary: Boundary) => {
    if (boundaryIndexToEditTypeOrComment !== undefined) {
      const updatedBoundaries = boundaries.map((boundary, i) =>
        i === boundaryIndexToEditTypeOrComment ? { ...updatedBoundary } : boundary,
      );

      setBoundaries(updatedBoundaries);
    }
    setOpenEditBoundaryTypeOrCommentModal(false);
    setBoundaryToEditTypeOrComment(undefined);
    setBoundaryIndexToEditTypeOrComment(undefined);
  };

  const isActionInProgress = requestPermitStatus.loading || requestRejectedPermitStatus.loading;

  return (
    <>
      <Segment basic vertical>
        <Grid columns={2} stackable>
          <Grid.Column>
            <Header
              as="h2"
              content={"Permit no. " + formData.number}
              subheader={
                permit
                  ? "Update " + permitType?.name.toLowerCase() + " details"
                  : sourcePermit
                    ? "Renew " + permitType?.name.toLowerCase() + " based on permit number " + sourcePermit.number
                    : "Request a new " + permitType?.name.toLowerCase()
              }
            ></Header>
          </Grid.Column>
          <Grid.Column textAlign="right">
            <Menu compact>
              <Menu.Item link onClick={handleBackToListClick}>
                Back to list
              </Menu.Item>
              {saveButtonVisible && (
                <Menu.Item link onClick={handleSave} disabled={isActionInProgress}>
                  Save
                  <Loader
                    style={{ marginLeft: "5px" }}
                    inline
                    active={addPermitRequestStatus.loading || editPermitStatus.loading}
                    size="mini"
                  />
                </Menu.Item>
              )}
              {(requestButtonVisible || requestRejectedPermitButtonsVisible) && (
                <Menu.Item link primary onClick={handleSubmitClick} disabled={isActionInProgress}>
                  Request
                  <Loader style={{ marginLeft: "5px" }} inline active={isActionInProgress} size="mini" />
                </Menu.Item>
              )}
            </Menu>
          </Grid.Column>
        </Grid>
      </Segment>
      <Segment attached="bottom">
        <Grid divided>
          <Grid.Column stretched mobile={5} tablet={5} computer={7} largeScreen={6} widescreen={6}>
            <Segment basic vertical>
              <Header as="h3">
                <Icon name="angle right"></Icon>
                <Header.Content>Basic details</Header.Content>
              </Header>
              <Divider></Divider>

              <RequestPermitForm
                affectedServicesOptions={affectedSerOptions()}
                formErrors={formErrors}
                errors={[
                  ...(calibrationCertificateErrors ? Object.values(calibrationCertificateErrors) : []),
                  ...(documentErrors ? Object.values(documentErrors) : []),
                  ...(sitePhotoErrors ? Object.values(sitePhotoErrors) : []),
                  ...(addPermitRequestStatus.errors ? addPermitRequestStatus.errors : []),
                  ...(editPermitStatus.errors ? editPermitStatus.errors : []),
                  ...(requestPermitStatus.errors ? requestPermitStatus.errors : []),
                  ...(requestRejectedPermitStatus.errors ? requestRejectedPermitStatus.errors : []),
                  ...(boundaryErrors ? Object.values(boundaryErrors) : []),
                ]}
                formData={formData}
                onChange={handleFormChange}
                isNumberEditable={!permit && nextPermitNumber === 1}
                templateFields={resolvedTemplateFields.length > 0 ? resolvedTemplateFields : undefined}
                isAffectedServicesEnabled={isAffectedServicesEnabled}
                isAsBuiltEnabled={isAsBuiltEnabled}
                maxPermitDurationDays={maxPermitDurationDays}
                maxPermitValidInAdvanceDays={maxPermitValidInAdvanceDays}
              />
            </Segment>
          </Grid.Column>

          <Grid.Column
            mobile={4}
            tablet={4}
            computer={3}
            largeScreen={3}
            widescreen={2}
            style={{ backgroundColor: "#f8f8f8" }}
          >
            {optionToSelectThePermitTeam && (
              <PermitTeamForm
                formErrors={{}}
                errors={undefined}
                formData={permitTeamFormData}
                onChange={handlePermitTeamFormChange}
                registerAssignments={registerAssignments}
                permitTypeRoles={permitTypeRoles}
                showRequiredAsterisk={false}
              ></PermitTeamForm>
            )}

            {isCatAndGennyEnabled && (
              <PermitCalibrationCertificate
                calibrationCertificate={calibrationCertificate}
                setCalibrationCertificate={handleCalibrationCertificateChange}
                errors={undefined}
                permitTeam={permitTeamFormData.permitAssignments.filter((a) => a.orgUser)}
              ></PermitCalibrationCertificate>
            )}

            {showPermitBoundariesOnMap && (
              <PermitMap
                permitHasBoundaries={true}
                onPermitBoundariesClick={() => {
                  setPreviewBoundaries(true);
                  setPreviewDoc(undefined);
                  setPreviewSitePhoto(undefined);
                  setPreviewNewSitePhoto(undefined);
                }}
              />
            )}

            <PermitAttachmentBar
              documents={documents}
              selectedDocument={previewDoc}
              mandatoryDocTypes={mandatoryDocTypes}
              onDocClicked={handleDocClicked}
              onAddDoc={handleAddDoc}
              onRemoveDoc={handleRemoveDoc}
              addNewDocumentButtonVisible={addNewDocumentButtonVisible}
            ></PermitAttachmentBar>

            {isSitePhotoEnabled && (
              <PermitSitePhotos
                sitePhotos={sitePhotos}
                onSitePhotoClicked={handleSitePhotoClicked}
                onNewSitePhotoClicked={handleNewSitePhotoClicked}
                onAddSitePhotos={handleAddSitePhoto}
                onRemoveNewSitePhoto={handleRemoveNewSitePhoto}
                onRemoveSitePhoto={handleRemoveSitePhoto}
              ></PermitSitePhotos>
            )}
          </Grid.Column>

          <Grid.Column mobile={7} tablet={7} computer={6} largeScreen={7} widescreen={8} style={{ padding: "0px" }}>
            {previewDoc && <DocumentView containerId="requestPermitDocView" document={previewDoc} isPopup={false} />}
            {previewSitePhoto && (
              <PhotoViewer
                comments={previewSitePhoto.comments}
                dateCreated={previewSitePhoto.dateCreated}
                createdBy={previewSitePhoto.createdBy as OrgUser}
                url={previewSitePhoto.fileUrl}
                type={previewSitePhoto.type}
              />
            )}
            {previewNewSitePhoto && (
              <PhotoViewer
                comments={newSitePhotos.find((p) => p.fileMetaData?.id === previewNewSitePhoto.id)?.comments}
                file={previewNewSitePhoto.file}
              />
            )}
            {showPermitBoundariesOnMap && previewBoundaries && (
              <MapWithBoundaries
                items={projectBoundaries}
                onBoundariesEditCompleted={handleSaveBoundaries}
                onBoundariesEditStarted={handleBoundariesEditStarted}
                onEditBoundaryTypeOrComment={handleEditBoundaryTypeOrComment}
                boundaries={boundaries}
                withPopup={true}
                popUpHasAction={true}
              />
            )}
          </Grid.Column>
        </Grid>
      </Segment>

      <PermitDeclarationsModal
        open={openSubmitModal}
        onClose={handleSubmitModalClose}
        onConfirm={handleRequest}
        onChange={setRequestDeclarations}
        declarations={requestDeclarations}
        isActionInProgress={isActionInProgress}
      ></PermitDeclarationsModal>

      <EditBoundaryTypeOrCommentModal
        open={openEditBoundaryTypeOrCommentModal}
        boundary={boundaryToEditTypeOrComment}
        onClose={() => {
          setOpenEditBoundaryTypeOrCommentModal(false);
          setBoundaryToEditTypeOrComment(undefined);
          setBoundaryIndexToEditTypeOrComment(undefined);
        }}
        onSave={handleEditBoundaryTypeOrCommentModalSave}
      ></EditBoundaryTypeOrCommentModal>
    </>
  );
};

export default RequestPermit;
